import { useEffect } from 'react';
import { useChat } from '../../hooks/useChat';
import { MessageList } from './MessageList';
import { ChatInput } from './ChatInput';
import { SuggestionChips } from './SuggestionChips';
import { ExportButton } from '../export/ExportButton';
import { AlertCircle } from 'lucide-react';

interface ChatContainerProps {
  conversationId?: string;
  onConversationIdChange?: (id: string) => void;
  onConversationUpdate?: () => void;
  conversationTitle?: string;
}

export function ChatContainer({
  conversationId,
  onConversationIdChange,
  onConversationUpdate,
  conversationTitle,
}: ChatContainerProps) {
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    stopGeneration,
    loadConversation,
    conversationId: currentConversationId,
  } = useChat({
    conversationId,
    onConversationIdChange,
    onConversationUpdate,
  });

  // Load conversation when conversationId prop changes
  useEffect(() => {
    if (conversationId && conversationId !== currentConversationId) {
      loadConversation(conversationId);
    }
  }, [conversationId, currentConversationId, loadConversation]);

  return (
    <div className="flex h-full flex-col">
      {/* Header with export button */}
      {messages.length > 0 && (
        <div className="flex items-center justify-end border-b border-gray-200 px-4 py-[18px] sm:px-6">
          <ExportButton
            messages={messages}
            conversationTitle={conversationTitle || 'Nebula Conversation'}
            disabled={isLoading}
          />
        </div>
      )}

      {/* Show loading state when loading conversation with no messages yet */}
      {isLoading && messages.length === 0 ? (
        <div className="flex h-full items-center justify-center">
          <div className="text-sm text-gray-500">Loading conversation...</div>
        </div>
      ) : (
        <>
          <div className="min-h-0 flex-1">
            <MessageList messages={messages} isLoading={isLoading} />
          </div>

          {error && (
            <div className="mx-4 mb-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive sm:mx-6">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="flex-1">{error}</span>
            </div>
          )}

          <SuggestionChips onSelect={sendMessage} visible={messages.length === 0} />

          <div className="sticky bottom-0 z-10 bg-nova-dashboard-900">
            <ChatInput onSend={sendMessage} isLoading={isLoading} onStop={stopGeneration} />
          </div>
        </>
      )}
    </div>
  );
}
